import { cn } from "@/lib/utils";
import { SerpMatchRollupValue } from "./SerpMatchCell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChevronDown,
  ChevronRight,
  MoreVertical,
  PanelRightOpen,
  Trash2,
  Plus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { TruncatedText } from "@/components/ui/truncated-text";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import { avg, sum, RankBadge, GroupTrendSummary } from "./KeywordThemeShared";
import type { KeywordTheme } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";

export function ThemeHeaderRow({
  theme,
  themeKeywords,
  isCollapsed,
  onToggle,
  onOpenTheme,
  onAddKeyword,
  onDelete,
  readOnly,
  allSelected,
  someSelected,
  onToggleSelectAll,
}: {
  theme: KeywordTheme;
  themeKeywords: Keyword[];
  isCollapsed: boolean;
  onToggle: () => void;
  onOpenTheme: () => void;
  onAddKeyword: () => void;
  onDelete: () => void;
  readOnly?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleSelectAll?: (checked: boolean) => void;
}) {
  const avgRank = avg(themeKeywords.map((k) => k.hero_rank));
  const avgKps = avg(themeKeywords.map((k) => k.kps));
  const totalVol = sum(themeKeywords.map((k) => k.search_volume));
  const avgRel = avg(themeKeywords.map((k) => k.hero_relevance));
  // AUT-496: `avg` skips nulls, so a theme shows the mean of the keywords that have a value and a
  // dash when none does. Unweighted, matching the prototype and every other roll-up here.
  const avgSerpMatch = avg(themeKeywords.map((k) => k.serp_match_pct));

  const stickyWidth = readOnly ? "w-[208px] min-w-[208px]" : "w-[248px] min-w-[248px]";
  const stickyGrid = readOnly ? "grid grid-cols-[28px_minmax(0,1fr)] items-center" : "grid grid-cols-[40px_28px_minmax(0,1fr)] items-center";

  return (
    <TableRow
      className="bg-muted/40 hover:bg-muted/50 cursor-pointer border-b border-border"
      onClick={onToggle}
    >
      <TableCell sticky className={cn("py-0 px-0", stickyWidth)}>
        <div className={stickyGrid}>
          {!readOnly && (
            <span className="py-2 px-2 flex items-center justify-center">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={(checked) => onToggleSelectAll?.(!!checked)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select all keywords in "${theme.name}"`}
              />
            </span>
          )}
          <span className="py-2 flex items-center justify-center text-muted-foreground">
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
          <span className="py-2 px-3 flex items-center gap-2 min-w-0">
            <span className="min-w-0">
              <TruncatedText className="font-semibold text-sm">{theme.name}</TruncatedText>
              <span className="text-xs text-muted-foreground block">{theme.keywords.length} keywords</span>
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              onClick={(e) => { e.stopPropagation(); onOpenTheme(); }}
              title="View thematic grouping"
            >
              <PanelRightOpen className="h-3.5 w-3.5 text-primary" />
            </Button>
          </span>
        </div>
      </TableCell>
      <TableCell className="w-[90px] py-2 px-2 text-center">
        {avgRank != null ? (
          <div className="inline-flex items-center justify-center gap-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
            <RankBadge rank={avgRank} />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="w-[64px] py-2 px-2 text-center tabular-nums font-semibold">
        {avgKps != null ? (
          <div className="inline-flex items-center justify-center gap-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
            <span>{avgKps}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="w-[110px]" />
      <TableCell className="w-[110px] py-2 px-2 text-center tabular-nums font-semibold">
        {totalVol != null ? (
          <div className="inline-flex items-center justify-center gap-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase">Agr</span>
            <span>{formatMetricNumber(totalVol)}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="w-[110px]" />
      <TableCell className="w-[120px]" />
      <TableCell className="w-[60px] py-2 px-2 text-center tabular-nums font-semibold">
        {avgRel != null ? (
          <div className="inline-flex items-center justify-center gap-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase">Avg</span>
            <span>{avgRel}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      {/* AUT-496: its own cell, aligned with the SERP Match column on the keyword rows. */}
      <TableCell className="w-[90px] py-2 px-2 text-center tabular-nums font-semibold">
        <SerpMatchRollupValue pct={avgSerpMatch} />
      </TableCell>
      <TableCell className="w-[84px] min-w-[84px] py-2 px-1">
        <GroupTrendSummary keywords={themeKeywords} />
      </TableCell>
      <TableCell className="w-[115px]" />
      <TableCell className="w-[123px]" />
      <TableCell className="w-[138px]" />
      {!readOnly && (
        <TableCell className="w-[70px] py-1.5 px-2">
          <div className="flex items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAddKeyword(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add keyword to theme
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onOpenTheme(); }}>
                  <PanelRightOpen className="h-4 w-4 mr-2" />
                  Manage theme
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete theme
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
